import React, { useState } from 'react';
import classnames from 'classnames';

import style from './EntryTypeItemVisibilityButton.module.scss';

import { Icon, IconType } from '@components/Icon';
import { SelectButtonItem } from '@components/SelectButtonItem';
import { ClickOutsideMask } from '@components/ClickOutsideMask';
import { UserEntryTypeTemplateTestAttributes } from 'chaika-test-attributes';
import { translate } from '@chaika/i18n/helper';

export interface EntryTypeItemVisibilityButtonProps {
    hideItemOnPrint?: boolean;
    disabled?: boolean;
}

interface Props extends EntryTypeItemVisibilityButtonProps {
    onChangeVisibilityClick?(hideFromPatient: boolean): void;
}

export interface EntryTypeItemVisibilityButtonMenuItem {
    label: string;
    dataTest: string;
    iconType: IconType;
    onClick(): void;
}

export function EntryTypeItemVisibilityButton({ hideItemOnPrint, disabled, onChangeVisibilityClick }: Props) {
    const [isOpen, setIsOpen] = useState(false);

    const items = getMenuItems();
    const selectedItem = hideItemOnPrint ? items[1] : items[0];

    function handleToggleIsOpen(event?: React.MouseEvent<HTMLDivElement>) {
        event?.stopPropagation();

        setIsOpen(prev => !prev);
    }

    function getMenuItems(): EntryTypeItemVisibilityButtonMenuItem[] {
        return [
            {
                label: translate('showEverywhere'),
                dataTest: UserEntryTypeTemplateTestAttributes.ShowForPatient,
                iconType: IconType.SHOW,
                onClick: () => onChangeVisibilityClick?.(false),
            },
            {
                label: translate('hideFromPatient'),
                dataTest: UserEntryTypeTemplateTestAttributes.HideFromPatient,
                iconType: IconType.HIDE,
                onClick: () => onChangeVisibilityClick?.(true),
            },
        ];
    }

    return (
        <div
            className={classnames([style.root, isOpen && style.open, disabled && style.root_disabled])}
            onClick={!disabled ? handleToggleIsOpen : undefined}
        >
            <div className={style.icon}>
                <Icon type={selectedItem.iconType} />
            </div>
            {isOpen && (
                <ClickOutsideMask
                    transparentBackground={true}
                    onClickOutside={handleToggleIsOpen}
                />
            )}
            <div className={classnames([style.menuWrapper])}>
                {items.map(item => (
                    <SelectButtonItem
                        key={item.label}
                        dataTest={item.dataTest}
                        label={item.label}
                        startIconType={item.iconType}
                        isSelected={item.label === selectedItem.label}
                        toggleIsOpen={handleToggleIsOpen}
                        onItemClick={item.onClick}
                    />
                ))}
            </div>
        </div>
    );
}
